import { useCallback, useState } from "react";
import useSearchParamState from "@coworker/apprestructured/src/shared/hooks/useSearchParamState";
import { TestbuyDetailSheet } from "../TestbuyDetailSheet/TestbuyDetailSheet";
import { TestbuyArticleDetailSheet } from "../TestbuyArticleDetailSheet/TestbuyArticleDetailSheet";
import { useGetTestbuy } from "../../hooks/useTestbuyV2";

export const TestbuySheet = () => {
  const [{ testbuyId, testbuyArticleId }, setSearchParams] =
    useSearchParamState();
  // Only needed to restore `taskViewer` (the task this testbuy is linked to) when
  // closing the testbuy sheet. Shares the cache with TestbuyDetailSheet's own fetch.
  const { data: testbuy } = useGetTestbuy(testbuyId);

  // Remembers the last article id shown so TestbuyArticleDetailSheet stays mounted (with
  // visible=false) while testbuyArticleId is cleared, instead of being removed from the DOM
  // immediately - otherwise its closing animation never gets a chance to play. Adjusted
  // during render (not in an effect) to avoid an extra render before it takes effect -
  // see https://react.dev/learn/you-might-not-need-an-effect#adjusting-state-when-a-prop-changes.
  const [prevTestbuyArticleId, setPrevTestbuyArticleId] =
    useState(testbuyArticleId);
  const [lastArticleId, setLastArticleId] = useState(testbuyArticleId);
  if (testbuyArticleId !== prevTestbuyArticleId) {
    setPrevTestbuyArticleId(testbuyArticleId);
    if (testbuyArticleId) {
      setLastArticleId(testbuyArticleId);
    } else if (!testbuyId) {
      // The whole sheet unmounts once testbuyId is cleared, so there's no closing
      // animation to preserve for the article sheet in that case: reset it.
      setLastArticleId(null);
    }
  }
  const articleSheetId = testbuyArticleId ?? lastArticleId;

  const handleCloseTestbuy = useCallback(() => {
    setSearchParams((params) => {
      params.delete("testbuyId");
      // testbuyId and taskViewer are never both set at the same time: restore the task
      // viewer for the task this testbuy is linked to, if any.
      if (testbuy?.taskId) {
        params.set("taskViewer", testbuy.taskId);
      }
    });
  }, [setSearchParams, testbuy]);

  const handleCloseArticle = useCallback(() => {
    setSearchParams((params) => {
      params.delete("testbuyArticleId");
    });
  }, [setSearchParams]);

  if (!testbuyId) {
    return null;
  }

  return (
    <>
      <TestbuyDetailSheet
        visible
        onClose={handleCloseTestbuy}
        testbuyId={testbuyId}
      />

      {articleSheetId && (
        <TestbuyArticleDetailSheet
          visible={!!testbuyArticleId}
          onClose={handleCloseArticle}
          testbuyId={testbuyId}
          articleId={articleSheetId}
        />
      )}
    </>
  );
};
